import React, { useEffect, useMemo, useState } from "react"
import "./home.css"

import {
    Avatar,
    Button,
    Chip,
    Skeleton
} from "@mui/material"

import { useNavigate } from "react-router-dom"
import moment from "moment"
import _ from "lodash"

import { service_list, service__list_student } from "../utils"

import {
    useLazyGetInstitutionClassesQuery,
} from "../Redux/actions/classSetup.action"
import {
    useGetUserInstitutionQuery
} from "../Redux/actions/setting.action"

import {
    ArrowForward,
    School,
    AdminPanelSettingsOutlined,
    SettingsOutlined,
    OndemandVideoOutlined,
    PersonAddAltOutlined,
    BadgeOutlined,
    EventNoteOutlined,
    PaymentsOutlined,
    MenuBookOutlined,
    AssessmentOutlined,
    WorkspacePremiumOutlined,
    AssignmentOutlined,
    RecentActorsOutlined,
    BeachAccessOutlined,
    EventBusyOutlined,
    AppsOutlined,
    ApartmentOutlined,
    WarningAmberOutlined,
    RocketLaunchOutlined,
    ExpandMore,
    PlaceOutlined
} from "@mui/icons-material"

import StudentExams from "./StudentExamList"
import AdmitCards from "./AdmitCardList"
import MonthList from "./MonthList"
import ApplyLeave from "./Leave"

const EXPIRY_WARNING_DAYS = 30

/*
 * Presentation for each service in utils/service_list. Keyed by title so the
 * service definitions (routes, actions, help text) stay in one place.
 */
const SERVICE_META = {
    "Admission Management": { icon: <PersonAddAltOutlined />, color: "#4f46e5", description: "Admit new students and set up classes", usesClasses: true },
    "Examination Admit Card": { icon: <BadgeOutlined />, color: "#0891b2", description: "Generate and publish admit cards", usesClasses: true },
    "Exam Timetable Planning": { icon: <EventNoteOutlined />, color: "#7c3aed", description: "Create and manage exam schedules", usesClasses: true },
    "Monthly Fee": { icon: <PaymentsOutlined />, color: "#059669", description: "Collect fees and track payments", usesClasses: true },
    "Class Subject's": { icon: <MenuBookOutlined />, color: "#d97706", description: "Set up major and minor subjects", usesClasses: true },
    "Result Management": { icon: <AssessmentOutlined />, color: "#db2777", description: "Import and publish exam results", usesClasses: true },
    "Certificate Management": { icon: <WorkspacePremiumOutlined />, color: "#0d9488", description: "Issue certificates to students" },
    "Lecture Courses": { icon: <OndemandVideoOutlined />, color: "#2563eb", description: "Sell recorded courses to students" },
    "Test Report's": { icon: <AssignmentOutlined />, color: "#4f46e5", description: "Your exam reports" },
    "Admit Card": { icon: <RecentActorsOutlined />, color: "#0891b2", description: "Download your admit cards" },
    "Holiday Calendar": { icon: <BeachAccessOutlined />, color: "#059669", description: "Upcoming holidays" },
    "Apply Leave": { icon: <EventBusyOutlined />, color: "#d97706", description: "Request leave from school" }
}

const STUDENT_WIDGETS = {
    "Test Report's": StudentExams,
    "Admit Card": AdmitCards,
    "Holiday Calendar": MonthList,
    "Apply Leave": ApplyLeave
}

function getCurrentUser() {
    try {
        const user = localStorage.getItem("current_user")
        return user ? _.get(JSON.parse(user), "user_data", null) : null
    } catch (error) {
        return null
    }
}

// Admission Management lists its actions as plain labels; everything else
// already uses { label, to }
function getServiceActions(item) {
    if (item.title === "Admission Management") {
        return [
            { label: "Take Admission", to: item.to },
            { label: "Create Class", to: "/create_class" }
        ]
    }
    return (item.actions || []).filter((action) => action && action.to)
}

function greeting() {
    const hour = new Date().getHours()
    if (hour < 12) return "Good morning"
    if (hour < 17) return "Good afternoon"
    return "Good evening"
}

function ServiceCard({ item, user, classCount, classesLoaded }) {
    const navigate = useNavigate()
    const meta = SERVICE_META[item.title] || { icon: <AppsOutlined />, color: "#4f46e5" }
    const actions = getServiceActions(item)
    const tips = item.list || []
    const Widget = STUDENT_WIDGETS[item.title]
    const isCertificates = item.title === "Certificate Management"
    const [primary, ...secondary] = actions

    return (
        <article className="hm-card" style={{ "--accent": meta.color }}>
            <div className="hm-card__head">
                <div className="hm-card__icon">{meta.icon}</div>
                <div className="hm-card__heading">
                    <h3 className="hm-card__title">{item.title.replace("'s", "s")}</h3>
                    {meta.description && (
                        <p className="hm-card__description">{meta.description}</p>
                    )}
                </div>
                {meta.usesClasses && classesLoaded && (
                    <span className="hm-card__badge">
                        {classCount} {classCount === 1 ? "class" : "classes"}
                    </span>
                )}
            </div>

            {Widget && (
                <div className="hm-card__widget">
                    <Widget studentInfo={user} />
                </div>
            )}

            {isCertificates ? (
                <div className="hm-card__chips">
                    {actions.map((action) => (
                        <Chip
                            key={action.to}
                            label={action.label}
                            onClick={() => navigate(action.to)}
                            className="hm-chip"
                        />
                    ))}
                </div>
            ) : actions.length > 0 && (
                <div className="hm-card__actions">
                    <Button
                        variant="contained"
                        endIcon={<ArrowForward fontSize="small" />}
                        onClick={() => navigate(primary.to)}
                        className="hm-btn hm-btn--primary"
                    >
                        {primary.label}
                    </Button>
                    {secondary.map((action) => (
                        <Button
                            key={action.to}
                            variant="outlined"
                            onClick={() => navigate(action.to)}
                            className="hm-btn hm-btn--secondary"
                        >
                            {action.label}
                        </Button>
                    ))}
                </div>
            )}

            {tips.length > 0 && (
                <details className="hm-card__tips">
                    <summary>
                        How it works
                        <ExpandMore fontSize="small" />
                    </summary>
                    <ul>
                        {tips.map((tip) => (
                            <li key={tip}>{tip}</li>
                        ))}
                    </ul>
                </details>
            )}
        </article>
    )
}

export default function Home() {
    const navigate = useNavigate()
    const user = useMemo(getCurrentUser, [])

    const userType = _.get(user, "meta_data.user_type", null)
    const platformId = _.get(user, "meta_data.user_platform", null)
    const isStaff = userType === "owner" || userType === "admin"

    const [getClassesAction, getClassesState] = useLazyGetInstitutionClassesQuery()
    const institutionState = useGetUserInstitutionQuery(platformId, { skip: !platformId })

    useEffect(() => {
        if (!user) {
            navigate("/auth/login")
        } else if (platformId) {
            getClassesAction({ institution_ref: platformId })
        }
    }, [])

    const [now] = useState(() => moment())

    const classList = useMemo(() => {
        const list = _.get(getClassesState, "currentData.list", [])
        return Array.isArray(list) ? list : []
    }, [getClassesState])
    const classCount = classList.length
    const classesLoaded = getClassesState.isSuccess

    const institution = _.get(institutionState, "data.data", null)

    const services = userType === "student"
        ? service__list_student
        : isStaff
            ? service_list
            : []

    const fullName = user
        ? [user.firstname, user.lastname].filter(Boolean).join(" ") ||
          _.get(user, "full_name") ||
          _.get(user, "name") ||
          ""
        : ""
    const firstName = fullName.split(" ")[0]
    const initials = fullName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase() || "U"
    const role = userType ? _.startCase(userType) : "User"

    // Subscription expiry set by the platform owner in the client list
    const expiryDate = _.get(institution, "expiry_date", null)
    const daysLeft = expiryDate
        ? moment(expiryDate, "YYYY-MM-DD").diff(now.clone().startOf("day"), "days")
        : null
    const showExpiry = userType === "admin" && daysLeft !== null && daysLeft <= EXPIRY_WARNING_DAYS

    const needsInstitution = !platformId && userType !== "student" && userType !== "owner"
    const needsClasses = userType === "admin" && platformId && classesLoaded && classCount === 0

    const shortcuts = [
        userType === "owner" && { label: "Admin panel", icon: <AdminPanelSettingsOutlined />, to: "/admin" },
        userType !== "student" && { label: "Settings", icon: <SettingsOutlined />, to: "/setting" },
        { label: "Lecture portal", icon: <OndemandVideoOutlined />, to: "/learn" }
    ].filter(Boolean)

    const institutionLocation = institution
        ? [institution.district, institution.state].filter(Boolean).join(", ")
        : ""

    return (
        <div className="hm-page" role="main">

            {/* ================= WELCOME ================= */}

            <header className="hm-hero">
                <div className="hm-hero__main">
                    <Avatar className="hm-hero__avatar">{initials}</Avatar>
                    <div className="hm-hero__text">
                        <p className="hm-hero__date">{now.format("dddd, D MMMM YYYY")}</p>
                        <h1 className="hm-hero__title">
                            {greeting()}{firstName ? `, ${firstName}` : ""}
                        </h1>
                        <div className="hm-hero__meta">
                            <Chip size="small" label={role} className="hm-role-chip" />
                            {platformId && (
                                institutionState.isLoading ? (
                                    <Skeleton width={160} />
                                ) : institution ? (
                                    <span className="hm-hero__institution">
                                        <ApartmentOutlined fontSize="inherit" />
                                        <span className="hm-capitalize">{institution.name}</span>
                                        {institutionLocation && (
                                            <>
                                                <PlaceOutlined fontSize="inherit" />
                                                <span className="hm-capitalize">{institutionLocation}</span>
                                            </>
                                        )}
                                    </span>
                                ) : null
                            )}
                        </div>
                    </div>
                </div>

                <nav className="hm-hero__shortcuts" aria-label="Shortcuts">
                    {shortcuts.map((shortcut) => (
                        <Button
                            key={shortcut.to}
                            startIcon={shortcut.icon}
                            onClick={() => navigate(shortcut.to)}
                            className="hm-shortcut"
                        >
                            {shortcut.label}
                        </Button>
                    ))}
                </nav>
            </header>

            {/* ================= ALERTS ================= */}

            {showExpiry && (
                <div className={`hm-alert ${daysLeft < 0 ? "hm-alert--danger" : "hm-alert--warning"}`} role="alert">
                    <WarningAmberOutlined />
                    <div>
                        <strong>
                            {daysLeft < 0
                                ? "Your subscription has expired"
                                : daysLeft === 0
                                    ? "Your subscription expires today"
                                    : `Your subscription expires in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}`}
                        </strong>
                        <span>
                            {daysLeft < 0 ? "It ended on " : "It ends on "}
                            {moment(expiryDate, "YYYY-MM-DD").format("D MMM YYYY")}. Contact the platform team to renew.
                        </span>
                    </div>
                </div>
            )}

            {needsInstitution && (
                <div className="hm-setup">
                    <div className="hm-setup__icon"><RocketLaunchOutlined /></div>
                    <div className="hm-setup__text">
                        <h2>Set up your institution</h2>
                        <p>Add your school or college details to start managing admissions, exams, fees and results.</p>
                    </div>
                    <Button
                        variant="contained"
                        endIcon={<ArrowForward />}
                        onClick={() => navigate("/setting/platform")}
                        className="hm-btn hm-btn--primary"
                    >
                        Get started
                    </Button>
                </div>
            )}

            {needsClasses && (
                <div className="hm-setup hm-setup--soft">
                    <div className="hm-setup__icon"><School /></div>
                    <div className="hm-setup__text">
                        <h2>Create your first class</h2>
                        <p>Most services work class by class. Add a class to start taking admissions.</p>
                    </div>
                    <Button
                        variant="contained"
                        endIcon={<ArrowForward />}
                        onClick={() => navigate("/create_class")}
                        className="hm-btn hm-btn--primary"
                    >
                        Create class
                    </Button>
                </div>
            )}

            {/* ================= STATS ================= */}

            {isStaff && platformId && (
                <section className="hm-stats" aria-label="Overview">
                    <div className="hm-stat">
                        <div className="hm-stat__icon"><School /></div>
                        <div>
                            <span className="hm-stat__value">
                                {getClassesState.isLoading || getClassesState.isUninitialized ? <Skeleton width={30} /> : classCount}
                            </span>
                            <span className="hm-stat__label">Classes</span>
                        </div>
                    </div>
                    <div className="hm-stat">
                        <div className="hm-stat__icon"><AppsOutlined /></div>
                        <div>
                            <span className="hm-stat__value">{services.length}</span>
                            <span className="hm-stat__label">Services</span>
                        </div>
                    </div>
                    <div className="hm-stat">
                        <div className="hm-stat__icon"><EventNoteOutlined /></div>
                        <div>
                            <span className="hm-stat__value">
                                {expiryDate ? moment(expiryDate, "YYYY-MM-DD").format("D MMM YYYY") : "No end date"}
                            </span>
                            <span className="hm-stat__label">Subscription</span>
                        </div>
                    </div>
                </section>
            )}

            {/* ================= SERVICES ================= */}

            {services.length > 0 && (
                <section aria-labelledby="hm-services-title">
                    <div className="hm-section-head">
                        <h2 id="hm-services-title">
                            {userType === "student" ? "Your school" : "Services"}
                        </h2>
                        <p>
                            {userType === "student"
                                ? "Reports, admit cards, holidays and leave in one place."
                                : "Everything you need to run your institution."}
                        </p>
                    </div>

                    <div className="hm-grid">
                        {services.map((item) => (
                            <ServiceCard
                                key={item.title}
                                item={item}
                                user={user}
                                classCount={classCount}
                                classesLoaded={classesLoaded}
                            />
                        ))}
                    </div>
                </section>
            )}
        </div>
    )
}
